import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import ptBR from './locales/pt-BR.json';

// Languages of the app. To add one: create src/i18n/locales/<code>.json with the same keys as en.json, add it here
// and in backend/lang/ (see README, "Languages").
// `name` is the language written in itself, the way language menus are usually shown.
export const LOCALES = [
    { code: 'en', short: 'EN', name: 'English' },
    { code: 'pt-BR', short: 'PT', name: 'Português (Brasil)' },
];

const supported = (code) => LOCALES.some((locale) => locale.code === code);

// The first language of the browser's list that the app has (any Portuguese means pt-BR), otherwise English. So a
// browser set to ["en-US", "pt-BR"] gets English and one set to ["pt-BR", "en"] gets Portuguese. This is what is shown before there is an
// account language: on the login, signup and password pages. Nothing is stored in the browser: the language of a
// person lives in their account (Users.locale) and is applied when they log in or open the app.
const browserLocale = () => {
    const languages = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
    for (const language of languages) {
        const tag = String(language).toLowerCase();
        if (tag.startsWith('pt')) return 'pt-BR';
        if (tag.startsWith('en')) return 'en';
    }
    return 'en';
};

export const i18n = createI18n({
    legacy: false, // composition API: i18n.global.locale is a ref
    locale: browserLocale(),
    fallbackLocale: 'en',
    messages: { en, 'pt-BR': ptBR },
});

const applyToPage = (code) => {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', code);
    }
};
applyToPage(i18n.global.locale.value);

export const currentLocale = () => i18n.global.locale.value;

// Changes the language of the app (only for this session: it is not saved anywhere).
export const setLocale = (code) => {
    if (!supported(code)) {
        return;
    }
    i18n.global.locale.value = code;
    applyToPage(code);
};

// Back to the language of the browser (when somebody logs out).
export const resetLocale = () => setLocale(browserLocale());

// The language of an account, when it logs in or the app opens. Accounts created before the language existed have none:
// they keep the language of the browser until they choose one in Setup.
export const applyAccountLocale = (code) => {
    if (code) {
        setLocale(code);
    }
};

// Translate outside of templates (toasts, computed labels): reactive to language changes when read in a computed.
export const t = (key, params) => i18n.global.t(key, params || {});

// The words of an entity ("category", "recurringBill"...) for the generic messages of src/i18n/locales (crud.*):
// each language needs its own forms, e.g. Portuguese changes "adicionada/adicionado" with the gender.
export const entityParams = (entity) => ({
    name: t(`entities.${entity}.name`),
    Name: t(`entities.${entity}.Name`),
    this: t(`entities.${entity}.this`),
    added: t(`entities.${entity}.added`),
    edited: t(`entities.${entity}.edited`),
    deleted: t(`entities.${entity}.deleted`),
});
